import React, { useContext } from 'react';
import { DashboardContext } from '../../../context/DashboardContext';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { TrendingUp, Calendar, DollarSign } from 'lucide-react';
import { formatCurrency } from '../../../lib/utils';

export default function RevenueChart() {
  const { dashboardData } = useContext(DashboardContext);
  const { loading } = dashboardData;
  const revenueData = [...dashboardData.revenueHistory].sort(
  (a, b) => new Date(a.date) - new Date(b.date)
);


  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="h-80 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl"></div>
        </div>
      </div>
    );
  }

  // Custom tooltip with premium design
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-2xl border border-gray-200">
          <p className="text-sm font-bold text-gray-900 mb-3 border-b border-gray-100 pb-2">
            {payload[0].payload.date}
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-600"></div>
              <span className="text-xs text-gray-600 font-medium">Revenue:</span>
              <span className="text-sm font-bold text-emerald-600 ml-auto">
                {formatCurrency(payload[0].value)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600"></div>
              <span className="text-xs text-gray-600 font-medium">Orders:</span>
              <span className="text-sm font-bold text-blue-600 ml-auto">
                {payload[0].payload.orders}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate total for the period
  const totalRevenue = dashboardData.revenueHistory.reduce(
    (sum, day) => sum + day.revenue,
    0
  );

  // const avgRevenue = (totalRevenue / dashboardData.revenueHistory.length).toFixed(0);
  const avgRevenue = dashboardData.revenueHistory.length > 0 ? dashboardData.revenueHistory[0].averageRevenue: 0;

  return (
    <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-gray-100 overflow-hidden group hover:shadow-xl transition-shadow duration-300">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-500/5 to-green-500/5 rounded-full -mr-32 -mt-32" />

      {/* Header */}
      <div className="flex items-start justify-between mb-8 relative z-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Revenue Trend</h2>
          </div>
        <p className="text-sm text-gray-500 font-medium ml-12">
            {dashboardData.rangeLabel} performance overview
        </p>

        </div>

        {/* Stats Cards */}
        <div className="flex gap-4">
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                Total
              </p>
            </div>
            <p className="text-2xl font-bold text-emerald-700">
              {formatCurrency(totalRevenue)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-blue-600" />
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                Average
              </p>
            </div>
            <p className="text-2xl font-bold text-blue-700">
              {formatCurrency(avgRevenue)}
            </p>
            <p className="text-xs text-blue-600 font-medium mt-1">per day</p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative z-10">
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={revenueData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="date"
      tickFormatter={(date) => {
        const d = new Date(date);
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      }}
      axisLine={false}
      tickLine={false}
      tick={{ fontSize: 12, fill: '#6b7280' }}
    />
            <YAxis
              axisLine={false}
              tickLine={false}
          tick={{ fontSize: 12, fill: '#6b7280' }}
          tickFormatter={(value) => `₹${value.toLocaleString("en-IN")}`}
        />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#10b981"
              strokeWidth={3}
              fill="url(#colorRevenue)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom Stats Bar */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-100 relative z-10">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-600"></div>
            <span className="text-sm text-gray-600 font-medium">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600 font-medium">
              {dashboardData.revenueHistory.length} days
            </span>
          </div>
        </div>

        <div className="text-right">
  <p className="text-xs text-gray-500 font-medium">Daily Growth Rate</p>

  <p
    className={`text-lg font-bold ${
      dashboardData.revenueChange > 0
        ? "text-emerald-600"
        : dashboardData.revenueChange < 0
        ? "text-red-600"
        : "text-gray-600"
    }`}
  >
    {dashboardData.revenueChange > 0 ? "+" : ""}
    {dashboardData.revenueChange}%
  </p>
</div>

      </div>
    </div>
  );
}